import { TENANT_TYPE_NAMES } from "@closer/shared";
import { Spinner, useToast } from "@closer/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";

import { ChevronDownIcon, WorkspaceIcon } from "../components/icons";
import { ApiRequestError } from "../lib/apiClient";
import { useAuth } from "../lib/authContext";
import { TopBarPanel } from "./TopBarPanel";

/**
 * Üst Bardaki Çalışma Alanı seçici (CLOS-99 `[060]`).
 *
 * ### Açık kararın cevabı: `TopBarPanel`
 *
 * Kart teknik seçimi yazılım ekibine bırakıyor (*"overlay/drawer/popover uygulaması ve
 * istemci durum yönetimi seçilecektir"*). Seçim: `[059]`'da doğan ortak kabuk — masaüstünde
 * `Popover`, mobilde `Drawer`. Kendi kabuğunu yazsaydı Üst Bar panelleri yerden yere farklı
 * davranır ve kartın *"mobilde erişilebilir panel sunulur"* kriteri her panelde yeniden
 * çözülürdü (kriter 5).
 *
 * İstemci durumu: yalnız `open`. Alan listesi ve aktif bağlam TanStack Query'de duruyor —
 * ikinci bir kopya çıkarmak, sunucu gerçeğinin bayatlamasına izin verirdi.
 *
 * ### Liste HER AÇILIŞTA yeniden değerlendirilir
 *
 * Kartın çalışma zamanı koşulu. Mount'ta bir kez çekilip bırakılsaydı, oturum ortasında
 * askıya alınan bir üyelik seçilebilir görünmeye devam eder ve kullanıcı 403'e toslardı.
 *
 * ### ⚠ Seçim istemci listesine GÜVENMEZ
 *
 * Satır seçilebilir görünse bile geçişi `switchWorkspace` (`[043]`) sunucuda yeniden
 * doğruluyor. Reddedilirse bağlam DEĞİŞMİYOR; kullanıcı bulunduğu alanda kalıyor ve liste
 * tazeleniyor.
 *
 * ### ⚠ Yeni organizasyon yolu BURADA (CLOS-811)
 *
 * Panelde yalnız mevcut alanlar vardı. Onboarding'i bitirmiş bir kullanıcının `/tenants`
 * seçim ekranına ulaşabileceği TIKLANABİLİR hiçbir yol yoktu — o adrese giden yedi kod
 * referansının hepsi programatik yönlendirmeydi. Canlı panelde ölçüldü (27 Ağustos):
 * kullanıcı ikinci bir organizasyon, özellikle hukuk bürosu açamıyordu.
 *
 * Bunun bedeli yalnız eksik bir kısayol değildi: ürünün "organizasyon türü seçtirmediği"
 * sanıldı ve dört BLOCKER'lık bir kusur raporuna yol açtı. Seçim baştan beri oluşturma
 * penceresindeydi; eksik olan ona giden GİRİŞTİ.
 *
 * Yol kabuğun kendi seçim ekranına götürüyor, pencereyi burada AÇMIYOR: alan seçimi ve
 * oluşturma aynı ekranın işi (`[042]`) ve pencereyi popover içinde açmak odak tuzağı
 * üretirdi.
 *
 * ### Kaydedilmemiş değişiklik uyarısı
 *
 * Kartın *"kaydedilmemiş değişiklik uyarısı korunur"* kriteri bugün TETİKLENEBİLİR DEĞİL:
 * Üst Bar'ın altında düzenlenebilir tek bir yüzey yok (`/app` altında yalnız Ana Sayfa var).
 * Kaydı olmayan bir uyarı altyapısı kurmak, hiçbir kaybı önlemeyen ölü kod olurdu
 * (ADR-0014). Geçişin tek giriş noktası aşağıdaki `sec` — ilk düzenlenebilir yüzey
 * (`[072]`) geldiğinde uyarı oraya, tek yere bağlanır.
 */
export function WorkspaceSwitcher() {
  const { memberships, membershipsLoading, activeTenant, activeContext, switchWorkspace } =
    useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [gecilen, setGecilen] = useState<string | null>(null);

  function degistir() {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    /**
     * ⚠ `refetchQueries`, `invalidateQueries` DEĞİL. Invalidate yalnız bayat işaretliyor ve
     * çekimi gözlemciye bırakıyor; bu kod tabanında ölçüldü — çekim tetiklenmiyor, tazeleme
     * kâğıt üzerinde kalıyordu (`[043]`/`[044]` aynı tuzağa düşmüştü).
     */
    void queryClient.refetchQueries({ queryKey: ["memberships"] });
  }

  async function sec(tenantId: string) {
    setGecilen(tenantId);
    try {
      await switchWorkspace(tenantId);
      setOpen(false);
    } catch (error) {
      // Metin SUNUCUDAN geliyor; ekran kendi gerekçesini uydurmuyor (refleks 4). Ağ
      // seviyesinde bir kopmada sunucunun söyleyecek sözü yok, yedek metin o durum için.
      toast.error(error instanceof ApiRequestError ? error.message : "Çalışma alanına geçilemedi");
    } finally {
      setGecilen(null);
    }
  }

  /**
   * ⚠ Aktif alan LİSTEDEN eşleşerek doğuyor, `localStorage`'daki kimlikten değil. Kart:
   * *"son kullanılan alan bilgisi erişim kanıtı olarak kabul edilmez."* Kimlik doğrudan
   * gösterilseydi, kullanıcının yazabildiği bir değer ekranda aktif alanmış gibi görünürdü.
   */
  const aktif = activeTenant;

  /**
   * Rol AKTİF BAĞLAMDAN (`[043]`) okunuyor, üyelik listesinden değil: `can()` de aynı
   * kaynaktan besleniyor. Liste satırındaki rol bayat kalsaydı ekranda yazan rol ile
   * gerçekte geçerli yetkiler ayrışırdı. Bağlam henüz yüklenmediyse satır hiç çizilmiyor —
   * yanlış rol göstermektense hiç göstermemek doğru.
   */
  const rolSatiri = activeContext ? (activeContext.roleName ?? "Bu alanda rol atanmaz") : null;

  /** Seçenekler: yalnız SEÇİLEBİLİR ve aktif olmayan alanlar. */
  const secenekler = memberships.filter(
    (alan) => alan.selectable && alan.tenantId !== aktif?.tenantId,
  );

  const tetikleyici = (
    <button
      type="button"
      onClick={degistir}
      aria-label="Çalışma alanı değiştir"
      aria-haspopup="menu"
      aria-expanded={open}
      className="flex h-11 min-w-0 items-center gap-2.5 rounded-xl border border-border-default bg-surface px-3 text-left transition-colors hover:border-border-strong hover:bg-canvas focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
    >
      <WorkspaceIcon className="size-[18px] shrink-0 text-fg-soft" />
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-sm font-medium text-fg-heading">
          {aktif ? aktif.name : "Çalışma alanı seçilmedi"}
        </span>
        {aktif ? (
          <span className="truncate text-xs text-fg-soft">{TENANT_TYPE_NAMES[aktif.type]}</span>
        ) : null}
      </span>
      <ChevronDownIcon className="size-4 shrink-0 text-fg-faint" />
    </button>
  );

  return (
    <TopBarPanel
      open={open}
      onClose={() => setOpen(false)}
      title="Çalışma alanı"
      trigger={tetikleyici}
    >
      <div className="w-72 max-w-full">
        {aktif ? (
          <div className="border-b border-border-subtle px-4 py-3">
            <p className="text-[11px] font-semibold tracking-wide text-fg-soft uppercase">
              Aktif çalışma alanı
            </p>
            <p className="truncate text-sm font-semibold text-fg-heading">{aktif.name}</p>
            <p className="text-xs text-fg-soft">{TENANT_TYPE_NAMES[aktif.type]}</p>
            {rolSatiri ? <p className="text-xs text-fg-soft">{rolSatiri}</p> : null}
          </div>
        ) : null}

        {membershipsLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : secenekler.length === 0 ? (
          /* Askıdaki üyelikler burada HİÇ görünmüyor; gerekçeleriyle birlikte kanonik
             yerleri çalışma alanı seçim ekranı (`[042]`). */
          <p className="px-3 py-4 text-sm text-fg-soft">Başka çalışma alanınız yok</p>
        ) : (
          <ul className="py-1">
            {secenekler.map((alan) => (
              <li key={alan.tenantId}>
                <button
                  type="button"
                  onClick={() => void sec(alan.tenantId)}
                  disabled={gecilen !== null}
                  className="flex w-full min-w-0 flex-col px-3 py-2 text-left transition-colors hover:bg-muted focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary disabled:opacity-60"
                >
                  <span className="truncate text-sm text-fg">{alan.name}</span>
                  <span className="truncate text-xs text-fg-soft">
                    {TENANT_TYPE_NAMES[alan.type]}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {/*
          ⚠ Liste boş olsa da duruyor: yeni organizasyon açmak "başka alanım var mı"
          sorusundan bağımsız. Boş durumda gizleseydik, tek alanı olan kullanıcı için
          panel çıkmaza dönerdi.
        */}
        <div className="border-t border-border-default">
          <Link
            to="/tenants"
            onClick={() => setOpen(false)}
            className="flex w-full items-center px-3 py-2.5 text-sm font-medium text-primary transition-colors hover:bg-muted focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary"
          >
            Yeni Organizasyon
          </Link>
        </div>
      </div>
    </TopBarPanel>
  );
}
